/**
 * Fait : la seule lecture du fragment d'URL du produit — `#/projets/…`,
 *   `#/notice`, les liens de courriel. Elle rend une ROUTE, c'est-à-dire une
 *   intention nommée, jamais une chaîne à réinterpréter ailleurs.
 *
 *   Pas de dépendance de routage pour une dizaine d'écrans : le fragment
 *   suffit, et le bouton « précédent » du navigateur fonctionne quand même.
 *
 * Ne fait pas : elle ne décide d'aucun droit, ne demande rien au serveur, et
 *   n'affiche rien. Une adresse bien formée ne veut pas dire une adresse
 *   permise — c'est l'API qui tranche.
 */

/** Ce que le fragment d'URL désigne. */
export type Route =
  | { readonly ecran: 'projets' }
  | { readonly ecran: 'projet'; readonly id: string }
  | { readonly ecran: 'fil'; readonly id: string; readonly filId: string }
  | { readonly ecran: 'notice'; readonly chapitre: string | null }
  | { readonly ecran: 'administration' }
  | { readonly ecran: 'compte' }
  | { readonly ecran: 'consignes' }
  | { readonly ecran: 'argent'; readonly onglet: string | null }
  | { readonly ecran: 'vie-privee' }
  | { readonly ecran: 'oubli' }
  | { readonly ecran: 'mot-de-passe'; readonly jeton: string }
  | { readonly ecran: 'inscription'; readonly jeton: string }
  | { readonly ecran: 'lien-abime'; readonly pour: 'mot-de-passe' | 'inscription' };

/**
 * Les jetons qui arrivent par un lien de courriel — invitation, récupération.
 * La forme est celle du contrat partagé : 43 caractères en base64url. Une
 * adresse qui ne la respecte pas n'est pas une route, et l'on retombe sur
 * l'atelier plutôt que d'envoyer n'importe quoi au serveur.
 */
const JETON = '([A-Za-z0-9_-]{43})';

/** Lit la route dans le fragment — la notice, la liste, un projet, ou un fil. */
export function routeCourante(): Route {
  // La notice AVANT le reste, et sans exiger de session : elle doit se lire
  // même quand on ne sait pas encore entrer — c'est souvent là qu'on en a le
  // plus besoin.
  const notice = /^#\/notice(?:\/([a-z-]+))?$/.exec(location.hash);
  if (notice !== null) return { ecran: 'notice', chapitre: notice[1] ?? null };

  // La page qui dit ce que Clannik voit se lit AUSSI sans compte, et pour la
  // même raison que la notice : c'est avant d'écrire qu'on a besoin de savoir
  // ce qui est protégé, pas après (`D-033`).
  if (location.hash === '#/vie-privee') return { ecran: 'vie-privee' };

  // Les trois écrans que l'on atteint SANS compte ou sans pouvoir entrer.
  if (location.hash === '#/oubli') return { ecran: 'oubli' };

  const motDePasse = new RegExp(`^#/mot-de-passe/${JETON}$`).exec(location.hash);
  if (motDePasse !== null) return { ecran: 'mot-de-passe', jeton: motDePasse[1] ?? '' };

  const inscription = new RegExp(`^#/inscription/${JETON}$`).exec(location.hash);
  if (inscription !== null) return { ecran: 'inscription', jeton: inscription[1] ?? '' };

  /*
   * UN LIEN ABÎMÉ SE DIT. Un client de messagerie qui coupe une longue adresse
   * produit un fragment qui ne correspond plus à rien : sans ces deux lignes,
   * la personne retombe sur l'écran d'entrée sans un mot — exactement le
   * défaut que ce chantier avait fermé pour le lien intact, et qu'il laissait
   * ouvert pour le lien abîmé. On reconnaît l'INTENTION, même quand le jeton
   * est perdu.
   */

  if (location.hash.startsWith('#/mot-de-passe/')) {
    return { ecran: 'lien-abime', pour: 'mot-de-passe' };
  }
  if (location.hash.startsWith('#/inscription/')) {
    return { ecran: 'lien-abime', pour: 'inscription' };
  }

  if (location.hash === '#/administration') return { ecran: 'administration' };
  if (location.hash === '#/compte') return { ecran: 'compte' };
  if (location.hash === '#/consignes') return { ecran: 'consignes' };
  /*
   * L'ARGENT PORTE SON ONGLET DANS L'ADRESSE — `#/argent/les-modeles`.
   *
   * Rangé en mémoire, le choix d'onglet disparaissait au moindre rechargement
   * de page : on revenait sur « Mes dépenses » sans l'avoir demandé, et un
   * favori posé sur le catalogue ne rouvrait pas le catalogue. Mesuré le
   * 2026-08-27 sur trois cas d'épreuve qui rechargent la page.
   *
   * Dans l'adresse, il survit au rechargement, se met en favori, et le bouton
   * « précédent » du navigateur repasse d'un onglet à l'autre — ce que
   * l'atelier promet partout ailleurs.
   */
  const argent = /^#\/argent(?:\/([a-z-]+))?$/.exec(location.hash);
  if (argent !== null) return { ecran: 'argent', onglet: argent[1] ?? null };

  const fil = /^#\/projets\/([0-9a-f]{24})\/fils\/([0-9a-f]{24})$/.exec(location.hash);
  if (fil !== null) return { ecran: 'fil', id: fil[1] ?? '', filId: fil[2] ?? '' };

  const trouve = /^#\/projets\/([0-9a-f]{24})$/.exec(location.hash);
  return trouve === null ? { ecran: 'projets' } : { ecran: 'projet', id: trouve[1] ?? '' };
}
